import React, { useState } from 'react';
import {
  Box, TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Button, ButtonGroup, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const DATE_RANGE_OPTIONS = [
  { value: '24hr', label: '24HR' },
  { value: '7days', label: '7 DAYS' },
  { value: '30days', label: '30 DAYS' },
  { value: '90days', label: '90 DAYS' },
  { value: 'custom', label: 'CUSTOM' },
];

const ProTableFilters = ({ filters = [], state, actions }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const searchFilter = filters.find((f) => f.type === 'search');
  const dateRangeFilter = filters.find((f) => f.type === 'dateRange');
  const dropdownFilters = filters.filter((f) => f.type === 'status' || f.type === 'select');

  const handleDateRangeClick = (value) => {
    if (value === 'custom') {
      setCustomDateOpen(true);
    } else {
      state.setDateRange(value);
    }
  };

  const handleCustomDateApply = () => {
    if (customStart && customEnd) {
      state.setCustomDates({ start: customStart, end: customEnd });
      state.setDateRange('custom');
      setCustomDateOpen(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        {/* Search */}
        {searchFilter && (
          <TextField
            size="small"
            placeholder={searchFilter.placeholder || 'Search...'}
            value={state.search}
            onChange={(e) => state.setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && state.handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: '1 1 200px', minWidth: 200, '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
          />
        )}

        {/* Dropdown filters */}
        {dropdownFilters.map((f) => (
          <FormControl key={f.key} size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 32 } }}>
            <InputLabel sx={{ fontSize: '0.8125rem' }}>{f.label || f.key}</InputLabel>
            <Select
              value={state.filterValues[f.key] || ''}
              label={f.label || f.key}
              onChange={(e) => state.setFilterValue(f.key, e.target.value)}
              sx={{ fontSize: '0.8125rem' }}
            >
              <MenuItem value="">{f.allLabel || 'All'}</MenuItem>
              {(f.options || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        {/* Date Range */}
        {dateRangeFilter && !isMobile && (
          <ButtonGroup size="small" sx={{ flexShrink: 0, height: 32 }}>
            {DATE_RANGE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={state.dateRange === opt.value ? 'contained' : 'outlined'}
                onClick={() => handleDateRangeClick(opt.value)}
                sx={{
                  bgcolor: state.dateRange === opt.value ? 'secondary.main' : 'transparent',
                  borderColor: 'secondary.main',
                  color: state.dateRange === opt.value ? 'white' : 'secondary.main',
                  '&:hover': {
                    bgcolor: state.dateRange === opt.value ? 'secondary.dark' : 'action.hover',
                    borderColor: 'secondary.main',
                  },
                  fontWeight: 600,
                  fontSize: '0.6875rem',
                  px: 1.5,
                }}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        )}

        {/* Search + Refresh + Actions */}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Tooltip title="Search">
            <IconButton size="small" onClick={state.handleSearch} color="primary"
              sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={state.refresh}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {actions}
        </Box>
      </Box>

      {/* Custom Date Dialog */}
      {dateRangeFilter && (
        <Dialog open={customDateOpen} onClose={() => setCustomDateOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Select Date Range</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Start Date" type="date" value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }} fullWidth size="small" />
              <TextField label="End Date" type="date" value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }} fullWidth size="small" />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCustomDateOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCustomDateApply}
              disabled={!customStart || !customEnd}>Apply</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default ProTableFilters;
